import { IndexMetaData, StockSchemaData } from '../Stocks/Widgets/types'
import { getBreadCrumbSchema } from '@/components/Common/SEO/BreadcrumbSchema'
import { DOMAIN } from '@/root/constants'
import { INDSTOCKS_HOST, IND_STOCKS_DOMAIN } from '@/root/constants/common'

function getBreadCrumbListObj(entity_class?: string, host?: string) {
  switch (entity_class) {
    case 'ETF':
      return {
        label: 'ETFs',
        link:
          host === INDSTOCKS_HOST
            ? `${IND_STOCKS_DOMAIN}/stocks/etf`
            : `${DOMAIN}/stocks/etf`,
      }
    case 'INDEX':
      return {
        label: 'Indices',
        link:
          host === INDSTOCKS_HOST
            ? `${IND_STOCKS_DOMAIN}/indices`
            : `${DOMAIN}/indices`,
      }
    default:
      return {
        label: 'Stocks',
        link:
          host === INDSTOCKS_HOST
            ? `${IND_STOCKS_DOMAIN}/stocks`
            : `${DOMAIN}/stocks`,
      }
  }
}

export const seoDetailbreadCrumbList = (
  name?: string,
  entity_class?: string,
  host?: string,
  isGlobal?: boolean,
  useShortName?: boolean,
  short_name?: string,
) => [
  {
    label: 'Home',
    link: host === INDSTOCKS_HOST ? IND_STOCKS_DOMAIN : DOMAIN,
  },
  getBreadCrumbListObj(entity_class, host),
  ...(isGlobal
    ? [
        {
          label: 'Global Indices',
          link: 'https://www.indmoney.com/indices/global-indices',
        },
      ]
    : []),
  { label: useShortName ? `${short_name} Share Price` : name ?? '' },
]
export const fnoSeoDetailbreadCrumbList = (name?: string, host?: string) => [
  {
    label: 'Home',
    link: host === INDSTOCKS_HOST ? IND_STOCKS_DOMAIN : DOMAIN,
  },
  {
    label: 'Futures & Options',
    link:
      host === INDSTOCKS_HOST
        ? `${IND_STOCKS_DOMAIN}/futures-and-options`
        : `${DOMAIN}/futures-and-options`,
  },
  { label: name ?? '' },
]

type TGenericSchema = {
  [key: string]: string | TGenericSchema | Array<TGenericSchema>
}

const getFinancialProductSchema = ({
  stockSchemaData,
  relativepath,
}: {
  stockSchemaData: StockSchemaData
  relativepath: string
}): TGenericSchema => {
  const { name, symbol, logo, price } = stockSchemaData
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: `${name}`, // COMPANY NAME
    identifier: `${symbol}`, // COMPANY TICKER
    url: `${DOMAIN}${relativepath}`, // L2 Page URL
    image: `${logo}`, // Stock Ticker Image ( else fallback image )
    description: `Comprehensive information on ${name} stock, including current share price, historical data, financial ratios, and market insights.`, // Same as company description on L2
    provider: {
      '@type': 'Organization',
      name: 'INDmoney',
      url: `${DOMAIN}`, // INDmoney.com
    },
    offers: {
      '@type': 'Offer',
      price: `${price}`,
      priceCurrency: 'INR',
      url: `${DOMAIN}${relativepath}`,
      // "priceValidUntil": `${new Date().toLocaleString()}` // Today’s date -> 26-08-2024
    },
    potentialAction: {
      '@type': 'BuyAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${DOMAIN}${relativepath}`, // L2 page URl
      },
    },
    mainEntityOfPage: `${DOMAIN}${relativepath}`, // L2 page URL

    // Validate below custom properties on schema checker, then you can include the values from the static data //
  }
}

export const seoDataDetail = ({
  name,
  symbol,
  relativepath,
  entity_class,
  short_name,
  metaData = {},
  stockSchemaData,
  isGlobal = false,
  useShortName = false,
}: {
  name?: string
  symbol?: string
  relativepath?: string
  entity_class?: string
  short_name?: string
  metaData?: IndexMetaData
  stockSchemaData?: StockSchemaData
  isGlobal?: boolean
  useShortName?: boolean
}) => {
  const getSchemas = () => {
    const schemas = [
      getBreadCrumbSchema({
        breadcrumbs: seoDetailbreadCrumbList(
          name,
          entity_class,
          undefined,
          isGlobal,
          useShortName,
          short_name ?? name,
        ),
        resolvedUrl: `${DOMAIN}${relativepath}`,
      }),
    ]
    if (entity_class === 'STOCK' && stockSchemaData && relativepath) {
      const schemaObj = getFinancialProductSchema({
        stockSchemaData,
        relativepath,
      })
      schemas.push(schemaObj)
    }

    return schemas
  }

  let seoData = {
    title: `${short_name} Share Price Today – ${symbol} Stock Price, Live NSE/BSE Charts`,
    description: `${short_name} Share Price - Get ${name} Live NSE/BSE Share Price & historical returns, Performance, Financials, Market Cap, Analysts Rating, Target Price, Latest News and Insights to invest online with INDmoney.`,
    canonicalUrl: `${DOMAIN}${relativepath}`,
    schemas: getSchemas(),
    keywords: '',
  }
  if (entity_class === 'INDEX') {
    seoData = {
      ...seoData,
      title:
        metaData.title ??
        `${name} Index ${
          symbol ? `(${symbol})` : ''
        } - ${name} Index Live Chart | INDmoney`,
      description:
        metaData.description ??
        `${name} Index Live Today ${
          symbol ? `(${symbol})` : ''
        } - Get the latest information on ${name} Index companies, live price chart of ${name}, performance, index losers & gainers, and market data with INDmoney.`,
      keywords: `${short_name}, ${name}, ${name} Today`,
    }
  }

  return seoData
}

export const INDSTOCKSPOD = 'INDstocks'
